// components/modals/ProfileModal.tsx
import React, { useContext, useState } from "react";
import { Modal, View, Text, TouchableOpacity, StyleSheet } from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";
import { SessionContext } from "../../../contexts";
import { supabase } from "../../../utils/supabase";
import ClinicSettingsModal from "./ClinicSettingsModal";

type Props = {
  visible: boolean;
  onClose: () => void;
  onSignedOut?: () => void; // optional: navigate("/signin") from caller
};

const roleLabel = (raw?: string | null) => {
  switch (raw) {
    case "CLINIC_ADMIN":
      return "Clinic Admin";
    case "CLINIC_STAFF":
      return "Clinic Staff";
    case "PATIENT":
      return "Patient";
    default:
      return raw || "—";
  }
};

const ProfileModal: React.FC<Props> = ({ visible, onClose, onSignedOut }) => {
  const session = useContext(SessionContext)?.session;
  const [showSettings, setShowSettings] = useState(false);

  const clinicName = session?.clinicName || "—";
  const email = session?.userEmail || "—";
  const role = roleLabel(session?.role);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: "global" });
      if (error) {
        if (error.message?.includes("session_not_found")) {
          // Session already invalid, clear local state
          await supabase.auth.signOut({ scope: "local" });
        } else {
          throw error;
        }
      }
      onClose?.();
      onSignedOut?.();
    } catch (err) {
      // Fallback: clear local state anyway
      await supabase.auth.signOut({ scope: "local" });
      onClose?.();
      onSignedOut?.();
    }
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
  };

  const handleClose = () => {
    setShowSettings(false);
    onClose();
  };

  if (showSettings) {
    return (
      <ClinicSettingsModal visible={visible} onClose={handleCloseSettings} />
    );
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>Profile</Text>
            <TouchableOpacity
              style={styles.settingsButton}
              onPress={() => setShowSettings(true)}
            >
              <Ionicons name="settings-outline" size={22} color="#6B7280" />
            </TouchableOpacity>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Clinic</Text>
            <Text style={styles.value} numberOfLines={1}>
              {clinicName}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Email</Text>
            <Text style={styles.value} numberOfLines={1}>
              {email}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Role</Text>
            <Text style={styles.value}>{role}</Text>
          </View>

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.btn, styles.btnGhost]}
              onPress={handleClose}
            >
              <Text style={styles.btnGhostText}>Close</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btn, styles.btnPrimary]}
              onPress={handleSignOut}
            >
              <Text style={styles.btnPrimaryText}>Sign Out</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  card: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111827",
  },
  settingsButton: {
    padding: 6,
    borderRadius: 6,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#E5E7EB",
  },
  label: {
    color: "#6B7280",
    width: 90,
  },
  value: {
    flex: 1,
    color: "#111827",
    textAlign: "right",
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
  },
  btn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
  },
  btnGhost: {
    backgroundColor: "#F3F4F6",
  },
  btnGhostText: {
    color: "#374151",
    fontWeight: "600",
  },
  btnPrimary: {
    backgroundColor: "#EF4444",
  },
  btnPrimaryText: {
    color: "#fff",
    fontWeight: "700",
  },
});

export default ProfileModal;
